import styled from '@emotion/styled'
import { faBell, faCheckCircle, faExclamationTriangle, faShoppingCart } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { getAllOrders } from '../../../../api/shop.api'
import DashboardCard from '../components/DashboardCard'
import SkeletonLoader from '../components/SkeletonLoader'
import { NotificationData } from '../types/widget-data.type'

const AlertContainer = styled.div<{ type: 'warning' | 'success' }>`
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 16px;
    border-radius: 8px;
    border-left: 4px solid;
    background: ${({ type }) => (type === 'warning' ? '#fefce8' : '#f0fdf4')};
    border-color: ${({ type }) => (type === 'warning' ? '#ca8a04' : '#16a34a')};
`

const AlertIcon = styled.div<{ type: 'warning' | 'success' }>`
    font-size: 2rem;
    color: ${({ type }) => (type === 'warning' ? '#ca8a04' : '#16a34a')};
`

const AlertContent = styled.div`
    flex: 1;
`

const AlertTitle = styled.p<{ type: 'warning' | 'success' }>`
    font-size: 1.4rem;
    font-weight: 600;
    margin: 0 0 4px 0;
    color: ${({ type }) => (type === 'warning' ? '#854d0e' : '#166534')};
`

const AlertDescription = styled.p<{ type: 'warning' | 'success' }>`
    font-size: 1.2rem;
    margin: 0;
    color: ${({ type }) => (type === 'warning' ? '#a16207' : '#15803d')};
`

const NotificationsWidget = () => {
    const { t } = useTranslation()

    const { data: orders, isLoading } = useQuery({
        queryKey: ['orders'],
        queryFn: getAllOrders,
    })

    if (isLoading) {
        return <SkeletonLoader showHeader itemCount={1} itemHeight="80px" />
    }

    const data: NotificationData = {
        pendingOrders: orders?.filter((o: any) => o.status === 'PENDING').length || 0,
        lowStockProducts: [],
        tournamentAlerts: [],
    }

    return (
        <DashboardCard title={t('adminDashboard.widgets.notifications.title')} icon={<FontAwesomeIcon icon={faBell} />}>
            {data.pendingOrders > 0 ? (
                <AlertContainer type="warning">
                    <AlertIcon type="warning">
                        <FontAwesomeIcon icon={faExclamationTriangle} />
                    </AlertIcon>
                    <AlertContent>
                        <AlertTitle type="warning">
                            {t('adminDashboard.widgets.notifications.pendingOrders')}
                        </AlertTitle>
                        <AlertDescription type="warning">
                            <FontAwesomeIcon icon={faShoppingCart} style={{ marginRight: '4px' }} />
                            {data.pendingOrders} {t('adminDashboard.widgets.notifications.orders')}
                        </AlertDescription>
                    </AlertContent>
                </AlertContainer>
            ) : (
                <AlertContainer type="success">
                    <AlertIcon type="success">
                        <FontAwesomeIcon icon={faCheckCircle} />
                    </AlertIcon>
                    <AlertContent>
                        <AlertTitle type="success">{t('adminDashboard.widgets.notifications.noAlerts')}</AlertTitle>
                    </AlertContent>
                </AlertContainer>
            )}
        </DashboardCard>
    )
}

export default NotificationsWidget
